/**
 * Utilities for parsing PDB files and handling contig generation
 */

export interface PdbChainInfo {
  chainId: string;
  residueRange: {
    start: number;
    end: number;
  };
}

/**
 * Parse a PDB file and extract chain IDs and residue ranges
 * @param pdbContent - The content of the PDB file as a string
 * @returns Array of chain information with residue ranges
 */
export function parsePdbStructure(pdbContent: string | null): PdbChainInfo[] {
  if (!pdbContent) {
    console.log('parsePdbStructure: No PDB content provided');
    return [];
  }

  console.log('parsePdbStructure: Starting to parse PDB content');
  console.log('parsePdbStructure: Content length', pdbContent.length);
  console.log('parsePdbStructure: First 100 chars', pdbContent.substring(0, 100));

  const lines = pdbContent.split('\n');
  console.log('parsePdbStructure: Number of lines', lines.length);
  
  const chainMap = new Map<string, { min: number; max: number }>();

  // Process ATOM and HETATM records
  let atomCount = 0;
  for (const line of lines) {
    if (line.startsWith('ATOM  ') || line.startsWith('HETATM')) {
      atomCount++;
      try {
        // Extract chain ID (column 22, 1-indexed in PDB format)
        const chainId = line.substring(21, 22).trim();
        
        // Parse residue number (columns 23-26)
        const resNum = parseInt(line.substring(22, 26).trim(), 10);
        
        if (chainId && !isNaN(resNum)) {
          if (!chainMap.has(chainId)) {
            chainMap.set(chainId, { min: resNum, max: resNum });
          } else {
            const current = chainMap.get(chainId)!;
            chainMap.set(chainId, {
              min: Math.min(current.min, resNum),
              max: Math.max(current.max, resNum)
            });
          }
        }
      } catch (error) {
        console.error('Error parsing PDB line:', error);
        // Continue parsing even if one line fails
      }
    }
  }
  
  console.log('parsePdbStructure: Found atom records', atomCount);
  console.log('parsePdbStructure: Found chains', chainMap.size);

  // Convert map to array of PdbChainInfo objects
  const result: PdbChainInfo[] = [];
  chainMap.forEach((range, chainId) => {
    result.push({
      chainId,
      residueRange: {
        start: range.min,
        end: range.max
      }
    });
  });

  // Sort by chain ID
  const sortedResult = result.sort((a, b) => a.chainId.localeCompare(b.chainId));
  console.log('parsePdbStructure: Final result', sortedResult);
  return sortedResult;
}

/**
 * Build a contig string from chain segments
 * @param segments - Array of chain segments
 * @returns Formatted contig string for RFDiffusion
 */
export function buildContigString(segments: Array<{
  chainId: string;
  start: number;
  end: number;
}>): string {
  if (segments.length === 0) {
    console.log('buildContigString: No segments provided');
    return '';
  }

  console.log('buildContigString: Creating contig string for segments', segments);
  
  // Format each segment as chainId+start-end
  const formattedSegments = segments.map(segment => 
    `${segment.chainId}${segment.start}-${segment.end}`
  );
  
  // Join segments with spaces
  const result = formattedSegments.join(' ');
  
  console.log('buildContigString: Final contig string', result);
  return result;
}

/**
 * Parse a contig string to extract chain segments
 * @param contigString - The contig string to parse
 * @returns Array of parsed chain segments
 */
export function parseContigString(contigString: string): Array<{
  chainId: string;
  start: number;
  end: number;
}> {
  if (!contigString) {
    console.log('parseContigString: Empty contig string provided');
    return [];
  }

  console.log('parseContigString: Parsing contig string:', contigString);
  
  const segments: Array<{
    chainId: string;
    start: number;
    end: number;
  }> = [];

  // Handle the special binder format with brackets
  if (contigString.startsWith('[') && contigString.endsWith(']')) {
    console.log('parseContigString: Detected RFDiffusion binder format with brackets');
    
    // Extract the content inside the brackets
    const innerContent = contigString.substring(1, contigString.length - 1);
    
    // Split by the chain break indicator '/0 '
    const parts = innerContent.split('/0 ');
    
    // We only care about the target part (before the chain break)
    if (parts.length > 0 && parts[0]) {
      const targetPart = parts[0];
      
      try {
        // Try to extract chain ID and residue range
        const match = targetPart.match(/^([A-Z]+)(\d+)-(\d+)$/);
        if (match) {
          const [, chainId, startStr, endStr] = match;
          const start = parseInt(startStr, 10);
          const end = parseInt(endStr, 10);
          
          if (!isNaN(start) && !isNaN(end)) {
            segments.push({
              chainId,
              start,
              end
            });
          }
        }
      } catch (error) {
        console.error('Error parsing binder target part:', error);
      }
    }
    
    return segments;
  }

  // Handle standard format with possible spaces or multiple segments
  return parseRegularContig(contigString);
}

/**
 * Helper function to parse a regular (non-binder) contig string
 */
function parseRegularContig(contigString: string): Array<{
  chainId: string;
  start: number;
  end: number;
}> {
  const segments: Array<{
    chainId: string;
    start: number;
    end: number;
  }> = [];

  // First split by spaces (for multiple segments)
  const spaceParts = contigString.split(/\s+/);
  
  for (const spacePart of spaceParts) {
    if (!spacePart.trim()) continue;
    
    // Check for chain breaks (segments separated by /)
    if (spacePart.includes('/')) {
      const chainBreakParts = spacePart.split('/');
      for (const part of chainBreakParts) {
        if (!part.trim()) continue;
        
        // Skip the special '0' part in binder format
        if (part === '0') continue;
        
        parseSegment(part, segments);
      }
    } else {
      // Regular segment
      parseSegment(spacePart, segments);
    }
  }
  
  console.log('parseRegularContig: Parsed segments:', segments);
  return segments;
}

/**
 * Helper function to parse a single segment and add it to the segments array
 */
function parseSegment(segment: string, segments: Array<{
  chainId: string;
  start: number;
  end: number;
}>): void {
  // Try to match different patterns
  
  // Pattern 1: A10-20 (one letter chain ID)
  let match = segment.match(/^([A-Za-z])(\d+)-(\d+)$/);
  
  // Pattern 2: AB10-20 (multi-letter chain ID)
  if (!match) {
    match = segment.match(/^([A-Za-z]+)(\d+)-(\d+)$/);
  }
  
  if (match) {
    const [, chainId, startStr, endStr] = match;
    const start = parseInt(startStr, 10);
    const end = parseInt(endStr, 10);
    
    if (!isNaN(start) && !isNaN(end)) {
      segments.push({
        chainId,
        start,
        end
      });
    } else {
      console.error('parseSegment: Invalid number in segment:', segment);
    }
  } else {
    console.error('parseSegment: Could not parse segment:', segment);
  }
} 